import classNames from "classnames";
import * as React from "react";
import { KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import ExternalLibrariesPopover from "./external-libraries-popover";
import { ExternalLibrariesBadge, ExternalLibrariesProps } from "./external-libraries.types";
import { ExternalLibrariesController, useExternalLibraries } from "./use-external-libraries";
import "./external-libraries-button.scss";

export type {
  ExternalLibrariesProps,
  LibraryItem,
  PackageItem,
  PackageVersion,
  LibraryView,
  LibrarySourceMode
} from "./external-libraries.types";

const DEFAULT_BADGE: ExternalLibrariesBadge = { content: "BETA", color: "yellow" as KuikaBadgeColor };

/**
 * "External Libraries" toolbar control for the code-action datasource editors (C#, Python, …).
 * A trigger button that opens a popover for browsing/installing registry packages and managing the
 * project's installed libraries. This component is the composition root; interaction logic lives in
 * `useExternalLibraries` and the views in their own files.
 */
const ExternalLibrariesButton: React.FC<ExternalLibrariesProps> = (props) => {
  const { disabled = false, badge = DEFAULT_BADGE, style } = props;
  const controller: ExternalLibrariesController = useExternalLibraries(props);

  const trigger: React.ReactNode = (
    <KuikaButton
      type="secondary"
      disabled={disabled}
      active={controller.visible}
      style={style}
      aria-label="External libraries"
      data-testid="externalLibrariesButton"
    >
      <span className="kuika-external-libraries__trigger">
        <KuikaSvg name="package" width={16} height={16} className="kuika-external-libraries__trigger-icon" />
        External Libraries
        <KuikaBadge content={badge.content} color={badge.color} />
        {!disabled && (
          <KuikaSvg
            name="chevron-down"
            type="arrows"
            width={14}
            height={14}
            className={classNames("kuika-external-libraries__trigger-chevron", {
              "kuika-external-libraries__trigger-chevron--open": controller.visible
            })}
          />
        )}
      </span>
    </KuikaButton>
  );

  if (disabled) return <>{trigger}</>;

  return (
    <KuikaPopover
      title="External Libraries"
      position="bottom"
      align="end"
      visible={controller.visible}
      onVisibleChange={controller.setVisible}
      content={
        <ExternalLibrariesPopover
          controller={controller}
          libraries={props.libraries}
          packages={props.packages}
          versions={props.versions}
          hasMorePackages={props.hasMorePackages}
          sourceMode={props.sourceMode ?? "registry"}
          modulePlaceholder={props.modulePlaceholder}
          moduleHelpText={props.moduleHelpText}
          moduleExamples={props.moduleExamples}
        />
      }
    >
      {trigger}
    </KuikaPopover>
  );
};

export default ExternalLibrariesButton;
